import React, { useEffect, useState } from "react";
import * as yup from "yup";
import {
  Card,
  CardContent,
  Typography,
  Link,
} from "@mui/material";
import { makeStyles } from "@mui/styles"
import { useFormikContext } from "formik";
import { Upload } from "../../../../models/Upload";
import { formatName } from "../../../../models/Name";
import { TFunction } from "i18next";
import { useTranslation, Trans } from "react-i18next";
import moment from "moment";
import { InfoDialog } from "../../../controls/InfoDialog/InfoDialog";
import { TermsOfUse } from "../../../../models/TermsOfUse";
import { UploadCheckbox } from "../../../controls/UploadCheckbox/UploadCheckbox";

export const StepTitle5 = (t: TFunction) => t("step5.title");
export const StepInstructions5 = (t: TFunction) => t("step5.instructions");
export const StepValidation5 = (t: TFunction) =>
  yup.object({
    termsOfUse: yup.boolean().oneOf([true]),
  });

const useStyles = makeStyles({
  card: {
    minWidth: 275,
  },
  title: {
    fontSize: 16,
    fontWeight: "bold",
  },
  pos: {
    marginBottom: 12,
  },
  termsOfUse: {
    marginLeft: "auto",
    marginRight: "auto",
  },
  termsOfUseLink: {
    fontWeight: "bold",
    textDecorationLine: "underline",
  },
});

interface Step5Props {
  termsOfUse: TermsOfUse;
}

export const Step5: React.FC<Step5Props> = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<Upload>();
  const [showTermsOfUse, setShowTermsOfUse] = useState(false);

  const handleTermsOfUse = () => {
    setShowTermsOfUse(true);
  };

  const onTermsOfUseClose = () => {
    setShowTermsOfUse(false);
  };

  useEffect(() => {
    // in case terms of use are disabled
    if (!props.termsOfUse.enabled) setFieldValue("termsOfUse", true);
  }, [props.termsOfUse.enabled, setFieldValue]);

  return (
    <>
      <Card className={classes.card}>
        <CardContent>
          <Typography className={classes.title} gutterBottom>
            {t("step1.title")}
          </Typography>
          <Typography className={classes.pos} variant="body2" component="p">
            {t("step1.name")}: {values.user.name}
            <br />
            {t("step1.email")}: {values.user.email}
          </Typography>
          <Typography className={classes.title} gutterBottom>
            {t("step2.title")}
          </Typography>
          <Typography className={classes.pos} variant="body2" component="p">
            {t("step2.receiver")}: {values.target.name}
            <br />
            {t("step2.message")}: {values.request.message}
          </Typography>
          <Typography className={classes.title} gutterBottom>
            {t("step4.title")}
          </Typography>
          <Typography className={classes.pos} variant="body2" component="p">
            {t("step4.patientname")}: {formatName(values.patient?.name)}
            <br />
            {t("step4.birthdate")}:{" "}
            {moment(values.patient?.birthdate).format("L")}
          </Typography>
          {props.termsOfUse.enabled && (
            <div className={classes.termsOfUse}>
              <UploadCheckbox
                name="termsOfUse"
                fullWidth={false}
                label={
                  <Trans
                    i18nKey="step5.termsOfUse.message"
                    components={[
                      <Link
                        href="#"
                        className={classes.termsOfUseLink}
                        variant="body2"
                        onClick={handleTermsOfUse}
                      />,
                    ]}
                  />
                }
              />
            </div>
          )}
        </CardContent>
      </Card>
      <InfoDialog
        text={props.termsOfUse.text}
        open={showTermsOfUse}
        onClose={onTermsOfUseClose}
      />
    </>
  );
};
